import axios from 'axios';
import type { LoginRequest, RegisterRequest, User } from '../types';
// Importar os tipos corretos para o assessment
import type { Question, AssessmentAttempt, UserAnswer, Result, AssessmentHistory, DetailedAssessmentResult } from '../types/assessment.types';
// Importar os tipos para admin de relatórios
import type { ReportContent, ProfileReportResponse, ProfilePreviewResponse } from '../types/admin.types';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase';


// --- Axios API Client ---
const apiClient = axios.create({
  baseURL: '/api/v2',
});

// Interceptor to add the auth token to every request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Interceptor to handle 401 Unauthorized responses
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      
      // Skip auth handling for login/register endpoints to avoid infinite loops
      if (!requestUrl.includes('/auth/login') && !requestUrl.includes('/auth/register')) {
        // Clear token and user data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        
        // Save current URL to redirect back after login
        const currentPath = window.location.pathname + window.location.search;
        localStorage.setItem('redirect_after_login', currentPath);
        
        // Redirect to login page
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);


// --- ApiService Class ---
class ApiService {
  
  // --- New Authentication Methods ---

  async login(credentials: LoginRequest): Promise<User> {
    const { data } = await apiClient.post<any>('/auth/login', credentials);
    
    // Exchange custom token for ID token using Firebase Auth
    if (data.token) {
      const userCredential = await signInWithCustomToken(auth, data.token);
      const idToken = await userCredential.user.getIdToken();
      localStorage.setItem('auth_token', idToken);
    }
    
    const normalizedUser = this.normalizeUser(data.user);
    localStorage.setItem('user_data', JSON.stringify(normalizedUser));
    localStorage.removeItem('redirect_after_login');

    return normalizedUser;
  }

  async register(data: RegisterRequest) {
    const response = await apiClient.post('/auth/register', data);
    
    // Exchange custom token for ID token using Firebase Auth
    if (response.data.token) {
      const userCredential = await signInWithCustomToken(auth, response.data.token);
      const idToken = await userCredential.user.getIdToken();
      localStorage.setItem('auth_token', idToken);
    }
    
    const normalizedUser = this.normalizeUser(response.data.user);
    localStorage.setItem('user_data', JSON.stringify(normalizedUser));

    return normalizedUser;
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Error during logout:', error);
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('redirect_after_login');
  }

  async getProfile(): Promise<User> {
    // Get user data from localStorage since we don't have a profile endpoint
    const userData = localStorage.getItem('user_data');
    if (!userData) {
      throw new Error('No user data found');
    }
    return JSON.parse(userData);
  }

  private normalizeUser(backendUser: any): User {
    return {
      id: backendUser.id || backendUser.ID,
      email: backendUser.email || backendUser.Email,
      name: backendUser.name || backendUser.Name,
      profile_image: backendUser.profile_image || backendUser.ProfileImageURL || '',
      subscription_tier: (backendUser.subscription_tier || backendUser.SubscriptionTier || 'free') as any,
      is_active: backendUser.is_active ?? backendUser.IsActive ?? true,
      last_login_at: backendUser.last_login_at || backendUser.LastLoginAt,
      created_at: backendUser.created_at || backendUser.CreatedAt,
      // Manter campos originais para compatibilidade
      ...backendUser
    };
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  // --- Assessment Methods using the correct types ---

  async getQuestions(): Promise<Question[]> {
    const { data } = await apiClient.get<Question[]>('/assessments/communication/questions');
    return data;
  }

  async startAssessment(): Promise<AssessmentAttempt> {
    try {
      const { data } = await apiClient.post<AssessmentAttempt>('/assessments/communication/start');
      return data;
    } catch (error: any) {
      // Check for assessment already completed error from backend
      if (error.response?.status === 400 || error.response?.status === 500) {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || '';
        if (errorMessage.includes('ASSESSMENT_ALREADY_COMPLETED') || errorMessage.includes('already completed')) {
          throw new Error('ASSESSMENT_ALREADY_COMPLETED');
        }
      }
      throw error;
    }
  }

  async submitResponse(assessmentId: string, answers: UserAnswer[]): Promise<any> {
    const { data } = await apiClient.post(`/assessments/communication/${assessmentId}/submit`, answers);
    return data;
  }

  async finalizeAssessment(assessmentId: string): Promise<Result> {
    const { data } = await apiClient.post<Result>(`/assessments/communication/${assessmentId}/finalize`);
    return data;
  }

  // getResults pode ser removido se finalizeAssessment já retorna os resultados

  // --- Assessment History Methods ---
  
  async getAssessmentHistory(): Promise<AssessmentHistory> {
    const { data } = await apiClient.get<AssessmentHistory>('/assessments/communication/history');
    return data;
  }

  async getAssessmentResult(assessmentId: string): Promise<DetailedAssessmentResult> {
    const { data } = await apiClient.get<DetailedAssessmentResult>(`/assessments/communication/${assessmentId}/result`);
    return data;
  }

  async getUserAssessmentStatus(): Promise<{
    hasCompleted: boolean;
    hasActive: boolean;
    activeAssessment?: AssessmentAttempt;
    completedAssessment?: DetailedAssessmentResult;
  }> {
    try {
      const { data } = await apiClient.get<any>('/assessments/communication/history');
      
      // Safety check for data structure and assessments array
      if (!data || typeof data !== 'object') {
        return {
          hasCompleted: false,
          hasActive: false
        };
      }
      
      const assessments = Array.isArray(data.assessments) ? data.assessments : [];
      
      const completedAssessment = assessments.find(a => a?.status === 'completed');
      const activeAssessment = assessments.find(a => a?.status === 'in_progress');
      
      let activeAssessmentDetails = null;
      if (activeAssessment) {
        // For active assessment, we need to get the full details to resume
        try {
          const response = await apiClient.get<AssessmentAttempt>(`/assessments/communication/${activeAssessment.id}`);
          activeAssessmentDetails = response.data;
        } catch (error) {
          console.warn('Failed to get active assessment details:', error);
        }
      }
      
      return {
        hasCompleted: !!completedAssessment,
        hasActive: !!activeAssessment,
        activeAssessment: activeAssessmentDetails || undefined,
        completedAssessment: completedAssessment || undefined
      };
    } catch (error) {
      console.error('Error getting user assessment status:', error);
      return {
        hasCompleted: false,
        hasActive: false
      };
    }
  }

  // === PROFILE REPORT METHODS ===
  
  async getProfileReport(profileId: string): Promise<ReportContent> {
    try {
      const response = await apiClient.get<ReportContent>(`/admin/content/profiles/${profileId}/report`);
      return response.data;
    } catch (error) {
      console.error(`Error getting report for profile ${profileId}:`, error);
      throw error;
    }
  }

  async updateProfileReport(profileId: string, reportContent: ReportContent): Promise<ProfileReportResponse> {
    try {
      const response = await apiClient.put<ProfileReportResponse>(
        `/admin/content/profiles/${profileId}/report`,
        reportContent
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating report for profile ${profileId}:`, error);
      throw error;
    }
  }

  async previewProfileReport(profileId: string, markdown: string): Promise<ProfilePreviewResponse> {
    try {
      const response = await apiClient.post<ProfilePreviewResponse>(
        `/admin/content/profiles/${profileId}/report/preview`,
        { markdown }
      );
      return response.data;
    } catch (error) {
      console.error(`Error previewing report for profile ${profileId}:`, error);
      throw error;
    }
  }

  // Helper method to get list of available profiles for admin
  // NOTE: This method is deprecated. Use evaluation_matrix.perfis from content instead.
  async getAvailableProfiles(): Promise<string[]> {
    console.warn('⚠️ apiService.getAvailableProfiles() is deprecated. Use content.evaluation_matrix.perfis instead.');
    return [];
  }
}

export const apiService = new ApiService();